'use client'

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Loader2, Check, X, Plus, Copy, CheckCircle2, Trash2, Globe, AlertCircle, Shield } from 'lucide-react'
import { whiteLabelService } from '@/services/whiteLabelService'
import { useAuthStore } from '@/stores/authStore'
import { useBrandingOrDefault } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles } from '@/utils/pageBranding'

export default function AdminWhiteLabelDomains() {
    const { user } = useAuthStore()
    const { branding } = useBrandingOrDefault()
    const queryClient = useQueryClient()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)

    const [selectedMerchantId, setSelectedMerchantId] = useState<string>('')
    const [newDomain, setNewDomain] = useState('')
    const [isAddingDomain, setIsAddingDomain] = useState(false)

    // Fetch merchants list
    const { data: merchantsData, isLoading: isMerchantsLoading } = useQuery({
        queryKey: ['admin-all-merchants'],
        queryFn: async () => {
            return whiteLabelService.getAllMerchants({ limit: 50 })
        },
    })

    const merchants = merchantsData?.results || []
    const isAdmin = user?.is_staff === true || user?.is_superuser === true || user?.role === 'admin'

    // Helper function to extract error message from API response
    const extractErrorMessage = (error: any, defaultMessage: string = 'Operation failed'): string => {
        if (error.response?.data) {
            const data = error.response.data

            // Check for field-specific errors
            if (data.domain) {
                return Array.isArray(data.domain) ? data.domain[0] : data.domain
            }
            if (data.merchant) {
                return Array.isArray(data.merchant) ? data.merchant[0] : data.merchant
            }
            if (data.detail) return data.detail
            if (data.message) return data.message

            // Check for generic non-field errors
            if (Array.isArray(data) && data.length > 0) {
                return Array.isArray(data[0]) ? data[0][0] : data[0]
            }
        }

        return error.message || defaultMessage
    }

    if (!isAdmin) {
        return (
            <div
                className="min-h-screen flex items-center justify-center p-6"
                style={pageStyles.container}
            >
                <div className="text-center">
                    <div
                        className="p-4 rounded-full w-fit mx-auto mb-4"
                        style={pageStyles.primaryBg}
                    >
                        <AlertCircle className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold mb-2" style={pageStyles.text}>
                        Access Denied
                    </h2>
                    <p style={pageStyles.textSecondary}>
                        Only platform admins can manage white-label domains
                    </p>
                    <p className="text-xs mt-4" style={pageStyles.textSecondary}>
                        Your role: {user?.role || 'unknown'} | is_staff: {user?.is_staff ? 'true' : 'false'}
                    </p>
                </div>
            </div>
        )
    }

    // Fetch merchant domains
    const { data: domainsData, isLoading } = useQuery({
        queryKey: ['admin-merchant-domains', selectedMerchantId],
        queryFn: async () => {
            if (!selectedMerchantId) return null
            return whiteLabelService.getMerchantDomainsAdmin(selectedMerchantId)
        },
        enabled: !!selectedMerchantId,
    })

    const domains = domainsData?.domains || []
    const merchantInfo = domainsData?.merchant

    // Create domain mutation
    const createDomainMutation = useMutation({
        mutationFn: async () => {
            if (!selectedMerchantId) throw new Error('No merchant selected')
            return whiteLabelService.createDomainAsAdmin(selectedMerchantId, newDomain)
        },
        onSuccess: (data) => {
            toast.success(`Domain ${data.domain} created successfully`)
            setNewDomain('')
            setIsAddingDomain(false)
            queryClient.invalidateQueries({ queryKey: ['admin-merchant-domains'] })
        },
        onError: (error: any) => {
            console.error('Domain creation error:', error)
            toast.error(extractErrorMessage(error, 'Failed to create domain'))
        },
    })

    // Verify domain mutation
    const verifyDomainMutation = useMutation({
        mutationFn: (domainId: string) =>
            whiteLabelService.verifyDomainAsAdmin(domainId),
        onSuccess: (data) => {
            if (data.is_verified) {
                toast.success('Domain verified successfully!')
            } else {
                toast.error(data.message || 'Domain verification failed')
            }
            queryClient.invalidateQueries({ queryKey: ['admin-merchant-domains'] })
        },
        onError: (error: any) => {
            toast.error(extractErrorMessage(error, 'Verification failed'))
        },
    })

    // Delete domain mutation
    const deleteDomainMutation = useMutation({
        mutationFn: (domainId: string) =>
            whiteLabelService.deleteDomainAsAdmin(domainId),
        onSuccess: () => {
            toast.success('Domain deleted successfully')
            queryClient.invalidateQueries({ queryKey: ['admin-merchant-domains'] })
        },
        onError: (error: any) => {
            toast.error(extractErrorMessage(error, 'Failed to delete domain'))
        },
    })

    // Set primary domain mutation
    const setPrimaryMutation = useMutation({
        mutationFn: (domainId: string) =>
            whiteLabelService.setPrimaryDomainAsAdmin(domainId),
        onSuccess: () => {
            toast.success('Primary domain updated')
            queryClient.invalidateQueries({ queryKey: ['admin-merchant-domains'] })
        },
        onError: (error: any) => {
            toast.error(extractErrorMessage(error, 'Failed to set primary domain'))
        },
    })

    // Toggle domain active mutation
    const toggleActiveMutation = useMutation({
        mutationFn: ({ domainId, isActive }: { domainId: string; isActive: boolean }) =>
            whiteLabelService.toggleDomainStatusAsAdmin(domainId, isActive),
        onSuccess: () => {
            toast.success('Domain status updated')
            queryClient.invalidateQueries({ queryKey: ['admin-merchant-domains'] })
        },
        onError: (error: any) => {
            toast.error(extractErrorMessage(error, 'Failed to update domain'))
        },
    })

    return (
        <div
            className="min-h-screen"
            style={pageStyles.container}
        >
            <div className="relative z-10 p-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-4 mb-4">
                        <div
                            className="p-3 rounded-lg"
                            style={pageStyles.primaryBg}
                        >
                            <Shield className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold" style={pageStyles.text}>
                                White-Label Domain Management
                            </h1>
                            <p style={pageStyles.textSecondary} className="mt-1">
                                Manage custom CNAME domains for any merchant
                            </p>
                        </div>
                    </div>
                </div>

                {/* Merchant Selection Card */}
                <div
                    className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm mb-6"
                    style={cardStyles}
                >
                    <div className="p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Globe className="w-5 h-5" style={pageStyles.primary} />
                            <h3 className="text-lg font-semibold" style={pageStyles.text}>
                                Select Merchant
                            </h3>
                        </div>

                        {isMerchantsLoading ? (
                            <div className="flex items-center gap-2 p-4" style={pageStyles.textSecondary}>
                                <Loader2 className="animate-spin" size={16} />
                                <span className="text-sm">Loading merchants...</span>
                            </div>
                        ) : (
                            <select
                                value={selectedMerchantId}
                                onChange={(e) => {
                                    setSelectedMerchantId(e.target.value)
                                    setNewDomain('')
                                }}
                                style={{
                                    backgroundColor: branding.surfaceColor,
                                    color: branding.textColor,
                                    borderColor: `${branding.textSecondaryColor}30`,
                                }}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-offset-0 outline-none transition"
                                onFocus={(e) => {
                                    e.currentTarget.style.borderColor = branding.primaryColor
                                    e.currentTarget.style.boxShadow = `0 0 0 3px ${branding.primaryColor}20`
                                }}
                            >
                                <option value="">-- Select a merchant --</option>
                                {merchants.map((merchant) => (
                                    <option key={merchant.id} value={merchant.id}>
                                        {merchant.company_name || merchant.name} ({merchant.email})
                                    </option>
                                ))}
                            </select>
                        )}
                        <p className="text-xs mt-3" style={pageStyles.textSecondary}>
                            {merchants.length} merchant{merchants.length !== 1 ? 's' : ''} available
                        </p>
                    </div>
                </div>

                {selectedMerchantId && (
                    <>
                        {/* Merchant Info Card */}
                        {merchantInfo && (
                            <div
                                className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm mb-6"
                                style={{
                                    ...cardStyles,
                                    borderLeftWidth: '4px',
                                    borderLeftColor: branding.primaryColor,
                                    backgroundColor: `${branding.primaryColor}08`,
                                }}
                            >
                                <div className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h2
                                                className="text-xl font-semibold"
                                                style={pageStyles.text}
                                            >
                                                {merchantInfo.name}
                                            </h2>
                                            <p
                                                className="text-sm mt-1"
                                                style={pageStyles.textSecondary}
                                            >
                                                ID: {merchantInfo.id}
                                            </p>
                                        </div>
                                        <Badge
                                            style={{
                                                backgroundColor: branding.primaryColor,
                                                color: '#fff',
                                            }}
                                        >
                                            {(domainsData?.count || 0)} Domain{(domainsData?.count || 0) !== 1 ? 's' : ''}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Add Domain Card */}
                        <div
                            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm mb-6"
                            style={cardStyles}
                        >
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <Plus className="w-5 h-5" style={pageStyles.primary} />
                                        <h3 className="text-lg font-semibold" style={pageStyles.text}>
                                            Add New Domain
                                        </h3>
                                    </div>
                                    {!isAddingDomain && (
                                        <Button
                                            onClick={() => setIsAddingDomain(true)}
                                            style={{
                                                backgroundColor: branding.primaryColor,
                                                color: '#fff',
                                            }}
                                            className="hover:opacity-90 transition"
                                        >
                                            <Plus size={18} className="mr-2" /> Add Domain
                                        </Button>
                                    )}
                                </div>

                                {isAddingDomain && (
                                    <div className="space-y-4 mt-6 pt-6 border-t" style={{ borderColor: `${branding.textSecondaryColor}20` }}>
                                        <div>
                                            <label
                                                className="block text-sm font-medium mb-2"
                                                style={pageStyles.text}
                                            >
                                                Domain Name
                                            </label>
                                            <Input
                                                placeholder="e.g., payments.yourdomain.com"
                                                value={newDomain}
                                                onChange={(e) => setNewDomain(e.target.value)}
                                                style={{
                                                    backgroundColor: branding.surfaceColor,
                                                    color: branding.textColor,
                                                    borderColor: `${branding.textSecondaryColor}30`,
                                                }}
                                                className="w-full border rounded-lg focus:ring-2 focus:ring-offset-0 outline-none"
                                            />
                                            <p className="text-xs mt-2" style={pageStyles.textSecondary}>
                                                Enter the custom CNAME domain for this merchant
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={() => createDomainMutation.mutate()}
                                                disabled={!newDomain || createDomainMutation.isPending}
                                                style={{
                                                    backgroundColor: branding.primaryColor,
                                                    color: '#fff',
                                                }}
                                                className="hover:opacity-90 transition disabled:opacity-50"
                                            >
                                                {createDomainMutation.isPending && <Loader2 className="animate-spin mr-2" size={16} />}
                                                Create Domain
                                            </Button>
                                            <Button
                                                variant="outline"
                                                onClick={() => setIsAddingDomain(false)}
                                                style={{
                                                    borderColor: `${branding.textSecondaryColor}30`,
                                                    color: branding.textColor,
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Domains List */}
                        {isLoading ? (
                            <div className="flex items-center justify-center p-12">
                                <Loader2 className="animate-spin" size={32} style={{ color: branding.primaryColor }} />
                            </div>
                        ) : domains.length === 0 ? (
                            <div
                                className="transition-all duration-300 border card backdrop-blur-sm p-12 text-center"
                                style={cardStyles}
                            >
                                <Globe
                                    className="w-12 h-12 mx-auto mb-4 opacity-50"
                                    style={{ color: branding.textSecondaryColor }}
                                />
                                <p style={pageStyles.textSecondary} className="mb-4">
                                    No domains configured for this merchant
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-4">
                                {domains.map((domain) => (
                                    <div
                                        key={domain.id}
                                        className={`transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm`}
                                        style={{
                                            ...cardStyles,
                                            borderLeftWidth: '4px',
                                            borderLeftColor: domain.is_primary
                                                ? branding.primaryColor
                                                : domain.is_verified
                                                    ? '#10b981'
                                                    : '#f59e0b',
                                            backgroundColor: domain.is_primary
                                                ? `${branding.primaryColor}08`
                                                : 'inherit',
                                        }}
                                    >
                                        <div className="p-6">
                                            {/* Domain Header */}
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex-1">
                                                    <h3
                                                        className="text-lg font-semibold flex items-center gap-2"
                                                        style={pageStyles.text}
                                                    >
                                                        {domain.domain}
                                                        {domain.is_primary && (
                                                            <Badge
                                                                style={{
                                                                    backgroundColor: branding.primaryColor,
                                                                    color: '#fff',
                                                                }}
                                                            >
                                                                Primary
                                                            </Badge>
                                                        )}
                                                        {domain.is_verified && (
                                                            <CheckCircle2
                                                                className="text-green-500"
                                                                size={20}
                                                            />
                                                        )}
                                                    </h3>
                                                    <p className="text-sm mt-1" style={pageStyles.textSecondary}>
                                                        Status: {domain.is_active ? 'Active' : 'Inactive'} • SSL:{' '}
                                                        {domain.ssl_enabled ? 'Enabled' : 'Disabled'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Verification Status */}
                                            <div
                                                className="space-y-2 p-4 rounded mb-4"
                                                style={{
                                                    backgroundColor: branding.surfaceColor,
                                                    borderColor: `${branding.textSecondaryColor}20`,
                                                    border: '1px solid',
                                                }}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-medium" style={pageStyles.text}>
                                                        Verification
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        {domain.is_verified ? (
                                                            <>
                                                                <Check className="text-green-500" size={18} />
                                                                <span className="text-sm text-green-600">
                                                                    Verified on {domain.verified_at?.split('T')[0]}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <X className="text-yellow-500" size={18} />
                                                                <span className="text-sm text-yellow-600">
                                                                    Not verified
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                {!domain.is_verified && domain.verification_code && (
                                                    <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                                                        <p className="text-xs font-medium text-yellow-800 mb-2">
                                                            DNS CNAME Record:
                                                        </p>
                                                        <div className="space-y-1 text-xs text-gray-700 font-mono">
                                                            <div>
                                                                <strong>Name:</strong> {domain.domain}
                                                            </div>
                                                            <div>
                                                                <strong>Type:</strong> CNAME
                                                            </div>
                                                            <div>
                                                                <strong>Value:</strong> pexiblocks.com
                                                            </div>
                                                            <div>
                                                                <strong>TTL:</strong> 3600
                                                            </div>
                                                        </div>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(
                                                                    `${domain.domain} CNAME pexiblocks.com`
                                                                )
                                                                toast.success('DNS record copied!')
                                                            }}
                                                            className="mt-2 w-full text-xs"
                                                        >
                                                            <Copy size={12} className="mr-1" /> Copy DNS Record
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Actions */}
                                            <div className="flex flex-wrap gap-2">
                                                {!domain.is_verified && (
                                                    <Button
                                                        size="sm"
                                                        style={{
                                                            backgroundColor: branding.primaryColor,
                                                            color: '#fff',
                                                        }}
                                                        onClick={() => verifyDomainMutation.mutate(domain.id)}
                                                        disabled={verifyDomainMutation.isPending}
                                                        className="hover:opacity-90 transition disabled:opacity-50"
                                                    >
                                                        {verifyDomainMutation.isPending && (
                                                            <Loader2 className="animate-spin mr-1" size={14} />
                                                        )}
                                                        Verify
                                                    </Button>
                                                )}

                                                {domain.is_verified && !domain.is_primary && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        style={{
                                                            borderColor: branding.primaryColor,
                                                            color: branding.primaryColor,
                                                        }}
                                                        onClick={() => setPrimaryMutation.mutate(domain.id)}
                                                        disabled={setPrimaryMutation.isPending}
                                                        className="hover:bg-opacity-10 transition disabled:opacity-50"
                                                    >
                                                        {setPrimaryMutation.isPending && (
                                                            <Loader2 className="animate-spin mr-1" size={14} />
                                                        )}
                                                        Set Primary
                                                    </Button>
                                                )}

                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    style={{
                                                        borderColor: `${branding.textSecondaryColor}30`,
                                                        color: branding.textColor,
                                                    }}
                                                    onClick={() =>
                                                        toggleActiveMutation.mutate({
                                                            domainId: domain.id,
                                                            isActive: !domain.is_active,
                                                        })
                                                    }
                                                    disabled={toggleActiveMutation.isPending}
                                                >
                                                    {domain.is_active ? 'Disable' : 'Enable'}
                                                </Button>

                                                <Button
                                                    size="sm"
                                                    variant="destructive"
                                                    onClick={() => {
                                                        if (confirm(`Delete domain ${domain.domain}?`)) {
                                                            deleteDomainMutation.mutate(domain.id)
                                                        }
                                                    }}
                                                    disabled={deleteDomainMutation.isPending}
                                                    className="ml-auto"
                                                >
                                                    <Trash2 size={14} /> Delete
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
